import React, { useState } from 'react';
import { useLootStore } from '../../stores/lootStore';
import { useInventoryStore } from '../../stores/inventoryStore';
import { useGameStore } from '../../stores/gameStore';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { useEncounterStore } from '../../stores/encounterStore';
import { useQuestStore } from '../../stores/questStore';
import { lootApiService } from '../../services/lootService';
import { openNpcContinuation } from '../../services/encounterService';
import { ItemIcon } from './ItemIcon';
import { InventoryPanel } from './InventoryPanel';
import type { IItemInstanceDTO, PendingLootItemDTO, GameConfigDTO, QuestDTO } from '@hexcrawl/shared-utils';

const ICON_SIZE = 48;

function LootItemRow({
  item,
  onTake,
  disabled,
  resourceIcons,
}: {
  item: PendingLootItemDTO;
  onTake: () => void;
  disabled: boolean;
  resourceIcons?: GameConfigDTO['resourceIcons'];
}) {
  if (item.type === 'gold') {
    const icon = resourceIcons?.gold;
    return (
      <div className="loot-item-card">
        {icon ? (
          <ItemIcon atlas={icon.atlas} frame={icon.frame} size={ICON_SIZE} />
        ) : (
          <span className="loot-item-icon-placeholder loot-icon-gold">🪙</span>
        )}
        <div className="loot-item-info">
          <span className="loot-item-name">{item.quantity} Gold</span>
        </div>
        <button className="loot-take-btn" onClick={onTake} disabled={disabled}>
          Take
        </button>
      </div>
    );
  }

  if (item.type === 'food') {
    const icon = resourceIcons?.food;
    return (
      <div className="loot-item-card">
        {icon ? (
          <ItemIcon atlas={icon.atlas} frame={icon.frame} size={ICON_SIZE} />
        ) : (
          <span className="loot-item-icon-placeholder loot-icon-food">🍖</span>
        )}
        <div className="loot-item-info">
          <span className="loot-item-name">{item.quantity} Food</span>
        </div>
        <button className="loot-take-btn" onClick={onTake} disabled={disabled}>
          Take
        </button>
      </div>
    );
  }

  const isQuestItem = item.def.isQuestItem ?? false;

  // equipment or a transferred quest-item instance
  return (
    <div className="loot-item-card" style={isQuestItem ? { border: '1px solid #c8a84a', background: 'rgba(40, 35, 10, 0.5)' } : undefined}>
      <ItemIcon atlas={item.def.icon.atlas} frame={item.def.icon.frame} size={ICON_SIZE} />
      <div className="loot-item-info">
        <span className="loot-item-name" style={isQuestItem ? { color: '#c8a84a' } : undefined}>{item.def.name}</span>
        {isQuestItem
          ? <span className="loot-item-slot" style={{ color: '#c8a84a', fontStyle: 'italic' }}>Quest Item</span>
          : <span className="loot-item-slot">{item.def.slot}</span>
        }
        {item.quantity > 1 && <span className="loot-item-qty">x{item.quantity}</span>}
      </div>
      <button
        className="loot-take-btn"
        onClick={onTake}
        disabled={disabled}
        title={disabled ? 'Inventory full — drop an item below to make room' : undefined}
      >
        Take
      </button>
    </div>
  );
}

export const LootOverlay: React.FC = () => {
  const pendingLoot = useLootStore((s) => s.pendingLoot);
  const setPendingLoot = useLootStore((s) => s.setPendingLoot);
  const clearPendingLoot = useLootStore((s) => s.clearPendingLoot);
  const inventory = useInventoryStore((s) => s.inventory);
  const equipment = useInventoryStore((s) => s.equipment);
  const gameState = useGameStore((s) => s.gameState);
  const setGameState = useGameStore((s) => s.setGameState);
  const gameConfig = useGameStore((s) => s.gameConfig);
  const fetchDiscoveries = useDiscoveryStore((s) => s.fetchDiscoveries);
  const gameId = gameState?.id;

  const [busy, setBusy] = useState(false);
  const [takingIndex, setTakingIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const finishLoot = () => {
    clearPendingLoot();
    // A quest turn-in deferred its reward to this loot window and queued a follow-on
    // conversation with the giver. Now that the reward is collected, open it.
    const followUpNpcId = useEncounterStore.getState().pendingFollowUpNpcId;
    if (followUpNpcId) {
      useEncounterStore.getState().setPendingFollowUpNpcId(null);
      void openNpcContinuation(followUpNpcId);
    } else if (!useEncounterStore.getState().activeEncounter) {
      useQuestStore.getState().flushBadgePulse();
    }
  };

  if (!pendingLoot || !gameId || !gameState) return null;

  // Capacity is measured against unequipped items only.
  const equippedIds = new Set(Object.values(equipment).filter((v): v is string => !!v));
  const inventoryFull = inventory.filter(i => !equippedIds.has(i.id)).length >= (gameConfig?.inventoryCapacity ?? 0);

  const applyLootResult = (newInventory: IItemInstanceDTO[], gold: number, food: number) => {
    const character = gameState.character
      ? { ...gameState.character, inventory: newInventory }
      : gameState.character;
    setGameState({ ...gameState, gold, food, character });
  };

  // A fetch quest can become ready_to_complete the moment its target item is
  // claimed; surface that in the journal and as a notification (the server flips
  // the status silently, so the claim response carries the promoted quests).
  const applyPromotedQuests = (quests?: QuestDTO[]) => {
    if (!quests || quests.length === 0) return;
    const questStore = useQuestStore.getState();
    for (const quest of quests) {
      questStore.addOrUpdateQuest(quest);
      questStore.showNotification(`Objective complete: ${quest.title}`);
    }
    questStore.queueBadgePulse();
  };

  const handleClaim = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await lootApiService.claimLoot(gameId);
      applyLootResult(result.inventory, result.gold, result.food);
      applyPromotedQuests(result.promotedQuests);
      if (result.skipped > 0) {
        if (result.pendingLoot) setPendingLoot(result.pendingLoot);
        setError(
          `Your bag is full — ${result.skipped} item${result.skipped !== 1 ? 's' : ''} couldn't be added. Drop something to make room.`,
        );
      } else {
        finishLoot();
      }
      if (gameId) void fetchDiscoveries(gameId);
    } catch (err: any) {
      setError(err.message || 'Failed to claim loot.');
    } finally {
      setBusy(false);
    }
  };

  const handleClaimItem = async (index: number) => {
    if (busy) return;
    setBusy(true);
    setTakingIndex(index);
    setError(null);
    try {
      const result = await lootApiService.claimItem(gameId, index);
      applyLootResult(result.inventory, result.gold, result.food);
      applyPromotedQuests(result.promotedQuests);
      if (result.pendingLoot) {
        setPendingLoot(result.pendingLoot);
      } else {
        finishLoot();
      }
      if (gameId) void fetchDiscoveries(gameId);
    } catch (err: any) {
      setError(err.message || 'Failed to take item.');
    } finally {
      setBusy(false);
      setTakingIndex(null);
    }
  };

  const handleDismiss = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await lootApiService.dismissLoot(gameId);
      finishLoot();
      if (gameId) void fetchDiscoveries(gameId);
    } catch (err: any) {
      setError(err.message || 'Failed to dismiss loot.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="loot-overlay">
      <div className="loot-panel">
        <div className="loot-loot-col">
          <h2 className="loot-title">Loot Found!</h2>

          <div className="loot-items-list">
            {pendingLoot.items.map((lootItem, index) => {
              const isEquipment = lootItem.type === 'equipment';
              const isTakeDisabled = busy || (isEquipment && inventoryFull);
              return (
                <LootItemRow
                  key={`${lootItem.type}-${index}`}
                  item={lootItem}
                  onTake={() => handleClaimItem(index)}
                  disabled={isTakeDisabled || takingIndex === index}
                  resourceIcons={gameConfig?.resourceIcons}
                />
              );
            })}
          </div>

          {error && <div className="loot-error">{error}</div>}

          <div className="loot-actions">
            <button className="loot-btn loot-btn-primary" onClick={handleClaim} disabled={busy}>
              Take All
            </button>
            <button className="loot-btn loot-btn-secondary" onClick={handleDismiss} disabled={busy}>
              Leave
            </button>
          </div>
        </div>

        <div className="loot-col-divider" />

        <div className="loot-inv-section">
          <div className="loot-inv-label">
            Inventory
            {inventoryFull && (
              <span className="loot-inv-full-badge">FULL — drop an item to make room</span>
            )}
          </div>
          <div className="loot-inv-panel">
            <InventoryPanel />
          </div>
        </div>
      </div>
    </div>
  );
};
